import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { AlertTriangle, ExternalLink, ChevronDown } from 'lucide-react';
import {
  reserveTermsSections,
  RESERVE_TERMS_VERSION,
  RESERVE_TERMS_EFFECTIVE_DATE,
} from '@/pages/ReserveTerms';

export default function ReserveTermsModal({
  isOpen,
  onClose,
  onAccept,
  isProcessing = false,
}) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const scrollRef = useRef(null);

  React.useEffect(() => {
    if (!isOpen) {
      setHasScrolledToBottom(false);
      setIsChecked(false);
    }
  }, [isOpen]);

  const checkScrollPosition = React.useCallback((element) => {
    if (!element) return;
    const isAtBottom =
      element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    if (isAtBottom) {
      setHasScrolledToBottom((prev) => (prev ? prev : true));
    }
  }, []);

  const handleScroll = (e) => {
    checkScrollPosition(e.target);
  };

  const handleScrollTouchEnd = React.useCallback(() => {
    checkScrollPosition(scrollRef.current);
  }, [checkScrollPosition]);

  React.useEffect(() => {
    if (!isOpen) return;
    const id = requestAnimationFrame(() => checkScrollPosition(scrollRef.current));
    return () => cancelAnimationFrame(id);
  }, [isOpen, checkScrollPosition]);

  const handleAccept = () => {
    if (isChecked && hasScrolledToBottom) {
      onAccept({
        version: RESERVE_TERMS_VERSION,
        acceptedAt: new Date().toISOString(),
      });
    }
  };

  const scrollToBottom = () => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={
          'bg-zinc-900 border-white/10 text-white max-w-2xl ' +
          'w-full p-0 pt-12 sm:pt-14 gap-0 flex flex-col overflow-hidden ' +
          /* Mobile: fullscreen sheet — one scroll region, normal modal=true (no fighting the page) */
          'max-sm:fixed max-sm:inset-0 max-sm:left-0 max-sm:top-0 max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none ' +
          /* Desktop: centered card */
          'sm:w-[calc(100vw-1.25rem)] sm:max-h-[min(92dvh,880px)] sm:top-[50%] sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 '
        }
      >
        <div
          ref={scrollRef}
          className={
            'flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain ' +
            'touch-pan-y [-webkit-overflow-scrolling:touch] ' +
            'px-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6 sm:pb-6 ' +
            'sm:max-h-[min(85dvh,800px)]'
          }
          style={{ touchAction: 'pan-y' }}
          onScroll={handleScroll}
          onTouchEnd={handleScrollTouchEnd}
          onTouchCancel={handleScrollTouchEnd}
        >
          <DialogHeader className="flex-shrink-0 space-y-1 pr-2 text-left">
            <DialogTitle className="text-lg sm:text-xl font-light leading-snug">
              Defera Reserve Terms & Conditions
            </DialogTitle>
            <DialogDescription className="text-white/60 font-normal text-xs sm:text-sm">
              Version {RESERVE_TERMS_VERSION} • Effective: {RESERVE_TERMS_EFFECTIVE_DATE}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-shrink-0 p-3 sm:p-4 rounded-xl bg-brand-500/10 border border-brand-500/20 my-3 sm:my-4">
            <div className="flex items-start gap-2.5 sm:gap-3">
              <AlertTriangle className="w-5 h-5 text-brand-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-brand-200 font-medium text-sm">Important Notice</p>
                <p className="text-brand-200/80 text-xs sm:text-sm mt-1 leading-relaxed">
                  Credits are non-withdrawable and have no cash value. By adding funds, you agree that all amounts convert to Defera Credits for travel purchases only.
                </p>
              </div>
            </div>
          </div>

          {!hasScrolledToBottom && (
            <div className="mb-3 flex justify-center">
              <button
                type="button"
                onClick={scrollToBottom}
                className="flex items-center gap-1 text-white/60 hover:text-white text-[11px] sm:text-xs transition-colors px-2 py-1 rounded-lg hover:bg-white/5"
              >
                <ChevronDown className="w-4 h-4 shrink-0" />
                <span className="leading-tight">Jump to end of terms</span>
              </button>
            </div>
          )}

          <div className="space-y-6 text-sm pr-1 sm:pr-2">
            <div className="text-white/80 font-normal leading-relaxed">
              <p>
                These Defera Reserve Terms & Conditions (&quot;Reserve Terms&quot;) govern your participation in the Defera Reserve program (&quot;Reserve&quot;), operated by Defera (&quot;Defera,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;).
              </p>
              <p className="mt-3">
                By adding funds to, accessing, or using Reserve, you agree to these Reserve Terms in addition to Defera&apos;s general Terms of Service.
              </p>
              <p className="mt-3 font-medium text-white/90">
                If you do not agree, you may not use Defera Reserve.
              </p>
            </div>

            {reserveTermsSections.map((section) => (
              <div key={section.id}>
                <h3 className="text-white font-medium mb-2">{section.title}</h3>
                <div className="text-white/80 font-normal leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </div>
            ))}

            <div className="h-4" />
          </div>

          <div className="flex-shrink-0 pt-3 sm:pt-4 mt-1 border-t border-white/10 space-y-3 sm:space-y-4">
            <div className="flex items-start gap-2.5 sm:gap-3">
              <Checkbox
                id="accept-terms"
                checked={isChecked}
                onCheckedChange={setIsChecked}
                disabled={!hasScrolledToBottom}
                className="mt-0.5 border-white/30 data-[state=checked]:bg-white data-[state=checked]:text-black shrink-0"
              />
              <label
                htmlFor="accept-terms"
                className={`text-xs sm:text-sm font-normal leading-relaxed cursor-pointer min-w-0 ${
                  hasScrolledToBottom ? 'text-white/85' : 'text-white/40'
                }`}
              >
                I have read and agree to the Defera Reserve Terms & Conditions. I understand that Credits are non-withdrawable and have no cash value.
              </label>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <Link
                to="/reserve/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs sm:text-sm text-white/60 hover:text-white inline-flex items-center gap-1.5 transition-colors py-1 order-2 sm:order-1 justify-center sm:justify-start"
              >
                View full terms
                <ExternalLink className="w-3.5 h-3.5 shrink-0" />
              </Link>

              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto order-1 sm:order-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onClose}
                  className="w-full sm:w-auto border border-white/20 text-white hover:bg-white/10 hover:text-white rounded-full bg-transparent h-11 sm:h-10"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleAccept}
                  disabled={!isChecked || !hasScrolledToBottom || isProcessing}
                  className="w-full sm:w-auto bg-white text-black hover:bg-white/90 rounded-full disabled:opacity-50 h-11 sm:h-10"
                >
                  {isProcessing ? 'Processing...' : 'I Agree & Continue'}
                </Button>
              </div>
            </div>

            {!hasScrolledToBottom && (
              <p className="text-white/60 text-[11px] sm:text-xs text-center px-1">
                Please scroll through the entire terms to enable acceptance
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
